import React, { useEffect, useState } from 'react';
import styles from './styles.module.css';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Loader from '../../../components/Loader';

// Функция получения веса по позиции
const getPositionWeight = (position) => {
  if (!position || position === 0) return 0;
  if (position >= 1 && position <= 3) return 1;
  if (position === 4) return 0.85;
  if (position === 5) return 0.6;
  if (position >= 6 && position <= 7) return 0.5;
  if (position >= 8 && position <= 9) return 0.3;
  if (position === 10) return 0.2;
  if (position >= 11 && position <= 15) return 0.1;
  if (position >= 16 && position <= 20) return 0.05;
  return 0; // 21+
};

// Функция расчета общей видимости для всех ключевых слов за конкретную дату
const calculateTotalVisibility = (data, date) => {
  let sumKP = 0; // сумма k * p
  let sumP = 0; // сумма p

  Object.values(data).forEach((entries) => {
    const entry = entries.find((e) => moment(e.date).format('DD.MM.YY') === date);
    if (entry && entry.rating !== null && entry.rating !== undefined && entry.total_shows !== null && entry.total_shows !== undefined) {
      const position = Number(entry.rating);
      const frequency = Number(entry.total_shows);
      
      if (Number.isFinite(position) && Number.isFinite(frequency) && position > 0 && frequency > 0) {
        const weight = getPositionWeight(position);
        sumKP += weight * frequency;
        sumP += frequency;
      }
    }
  });

  if (sumP === 0) return null;
  return (sumKP / sumP) * 100;
};

// Функция расчета видимости для отдельного ключевого слова за конкретную дату
const calculateKeywordVisibility = (entry, allData, date) => {
  if (!entry || entry.rating === null || entry.rating === undefined || entry.total_shows === null || entry.total_shows === undefined) {
    return null;
  }

  const position = Number(entry.rating);
  const frequency = Number(entry.total_shows);

  if (!Number.isFinite(position) || !Number.isFinite(frequency) || position === 0 || frequency === 0) {
    return null;
  }

  // Рассчитываем общую сумму частотностей для всех ключевых слов за эту дату
  let totalFrequency = 0;
  Object.values(allData).forEach((entries) => {
    const e = entries.find((e) => moment(e.date).format('DD.MM.YY') === date);
    if (e && e.total_shows !== null && e.total_shows !== undefined) {
      const freq = Number(e.total_shows);
      if (Number.isFinite(freq) && freq > 0) {
        totalFrequency += freq;
      }
    }
  });

  if (totalFrequency === 0) return null;

  const weight = getPositionWeight(position);
  // Вклад этого ключевого слова в общую видимость: (k * p / общая_частотность) * 100
  return (weight * frequency / totalFrequency) * 100;
};

// Функция расчета средней позиции для отфильтрованных ключевых слов за конкретную дату
const calculateAveragePosition = (filteredEntries, date) => {
  let sumPositions = 0;
  let count = 0;

  filteredEntries.forEach(([_, entries]) => {
    const entry = entries.find((e) => moment(e.date).format('DD.MM.YY') === date);
    if (entry && entry.rating !== null && entry.rating !== undefined) {
      const position = Number(entry.rating);
      if (Number.isFinite(position) && position > 0) {
        sumPositions += position;
        count++;
      }
    }
  });

  if (count === 0) return null;
  return sumPositions / count;
};

const PositionsTableAll = ({ selectedKeyword, selectedSite, provider, filter, onFilterChange }) => {
  const { positions, loading } = useSelector((state) => state.positions);
  const [sortColumn, setSortColumn] = useState(null); // дата колонки для сортировки
  const [sortDirection, setSortDirection] = useState(null); // 'desc' | 'asc' | null

  useEffect(() => {
    setSortColumn(null);
    setSortDirection(null);
  }, [selectedKeyword, selectedSite, provider]);

  const data = positions.keywords || {};
  const stats = positions.statistics || {};

  // Первая загрузка (нет данных) — показываем полный лоадер
  const hasStats = Object.keys(stats).length > 0;
  if (loading && !hasStats) {
    return (
      <div className={styles.loading}>
        <Loader />
      </div>
    );
  }

  // Получаем список всех уникальных дат
  const allDatesSet = new Set();
  Object.values(data).forEach((records) => {
    records.forEach((entry) => {
      allDatesSet.add(moment(entry.date).format('DD.MM.YY'));
    });
  });

  const sortedDates = Array.from(allDatesSet).sort(
    (a, b) => moment(b, 'DD.MM.YY') - moment(a, 'DD.MM.YY')
  );

  const visibilityValue = stats.visibility !== undefined && stats.visibility !== null
    ? stats.visibility
    : '?';

  // Данные уже отфильтрованы сервером
  const filteredEntries = Object.entries(data);

  // Используем среднюю позицию из статистики бэкенда
  const averagePositionValue = stats.average_position !== null && stats.average_position !== undefined 
    ? stats.average_position.toFixed(2) 
    : '?';

  const handleFilter = (type) => {

    const newFilter = type === filter ? null : type;
    
    onFilterChange(newFilter);

    if (newFilter === 'visibility' && sortedDates.length > 0) {
      setSortColumn(sortedDates[0]);
      setSortDirection('desc');
    }

    if (newFilter === null) {
      setSortColumn(null);
      setSortDirection(null);
    }

  };

  const handleResetFilter = () => {
    onFilterChange(null);
    setSortColumn(null);
    setSortDirection(null);
  };

  // Вспомогательная функция для создания мапа дат из записей
  const createDateMap = (entries) => {
    const map = {};
    entries.forEach((entry) => {
      const date = moment(entry.date).format('DD.MM.YY');
      map[date] = entry;
    });
    return map;
  };

  // Вспомогательная функция для проверки наличия данных
  const checkItemsExist = (itemA, itemB) => {
    if (!itemA && !itemB) return 0;
    if (!itemA) return 1;
    if (!itemB) return -1;
    return null; // оба элемента существуют
  };

  // Вспомогательная функция для обработки невалидных значений
  const handleInvalidValues = (valueA, valueB) => {
    if (valueA === null && valueB === null) return 0;
    if (valueA === null) return 1;
    if (valueB === null) return -1;
    return null; // оба значения валидны
  };

  // Компаратор для сортировки по видимости
  const createVisibilityComparator = (visibilityDate, allData) => {
    return ([keywordA, entriesA], [keywordB, entriesB]) => {
      const mapA = createDateMap(entriesA);
      const mapB = createDateMap(entriesB);

      const itemA = mapA[visibilityDate];
      const itemB = mapB[visibilityDate];

      const itemsCheck = checkItemsExist(itemA, itemB);
      if (itemsCheck !== null) return itemsCheck;

      const visibilityA = calculateKeywordVisibility(itemA, allData, visibilityDate);
      const visibilityB = calculateKeywordVisibility(itemB, allData, visibilityDate);

      const invalidCheck = handleInvalidValues(visibilityA, visibilityB);
      if (invalidCheck !== null) return invalidCheck;

      // Сортировка по убыванию (от большей видимости к меньшей)
      return visibilityB - visibilityA;
    };
  };

  // Компаратор для сортировки по ключевому слову
  const createKeywordComparator = (direction) => {
    return ([keywordA], [keywordB]) => {
      const a = keywordA.toLowerCase();
      const b = keywordB.toLowerCase();

      return direction === 'asc'
        ? a.localeCompare(b, 'ru')
        : b.localeCompare(a, 'ru');
    };
  };

  // Компаратор для сортировки по рейтингу (позициям)
  const createRatingComparator = (dateColumn, direction) => {
    return ([keywordA, entriesA], [keywordB, entriesB]) => {
      const mapA = createDateMap(entriesA);
      const mapB = createDateMap(entriesB);

      const itemA = mapA[dateColumn];
      const itemB = mapB[dateColumn];

      const itemsCheck = checkItemsExist(itemA, itemB);
      if (itemsCheck !== null) return itemsCheck;

      const ratingA = Number(itemA.rating);
      const ratingB = Number(itemB.rating);

      // Проверяем валидность рейтингов
      const validA = Number.isFinite(ratingA) ? ratingA : null;
      const validB = Number.isFinite(ratingB) ? ratingB : null;

      const invalidCheck = handleInvalidValues(validA, validB);
      if (invalidCheck !== null) return invalidCheck;

      // Сортировка по направлению
      return direction === 'desc'
        ? ratingB - ratingA
        : ratingA - ratingB;
    };
  };

  // Компаратор для сортировки по частотности
  const createFrequencyComparator = (direction) => {
    return ([keywordA, entriesA], [keywordB, entriesB]) => {
      // Получаем значение частотности за последнюю дату
      const getFrequency = (entries) => {
        if (!entries || entries.length === 0) return null;
        // Сортируем записи по дате (от новых к старым)
        const sortedEntries = [...entries].sort((a, b) => moment(b.date) - moment(a.date));
        // Ищем первое валидное значение total_shows
        for (const entry of sortedEntries) {
          const freq = Number(entry.total_shows);
          if (Number.isFinite(freq) && freq > 0) {
            return freq;
          }
        }
        return null;
      };

      const freqA = getFrequency(entriesA);
      const freqB = getFrequency(entriesB);

      const invalidCheck = handleInvalidValues(freqA, freqB);
      if (invalidCheck !== null) return invalidCheck;

      // Сортировка по направлению
      return direction === 'desc'
        ? freqB - freqA
        : freqA - freqB;
    };
  };

  let sortedFilteredEntries = [...filteredEntries];

  // Применяем соответствующий компаратор в зависимости от условий
  if (filter === 'visibility' && sortedDates.length > 0) {
    const visibilityDate = sortedDates[0];
    sortedFilteredEntries.sort(createVisibilityComparator(visibilityDate, data));
  } else if (sortColumn === 'keyword' && sortDirection) {
    sortedFilteredEntries.sort(createKeywordComparator(sortDirection));
  } else if (sortColumn === 'frequency' && sortDirection) {
    sortedFilteredEntries.sort(createFrequencyComparator(sortDirection));
  } else if (sortColumn && sortDirection) {
    sortedFilteredEntries.sort(createRatingComparator(sortColumn, sortDirection));
  }

  const handleDateClick = (date) => {
    // В режиме видимости сортировка отключена
    if (filter === 'visibility') return;
    
    if (sortColumn === date) {
      // Переключаем направление: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      // Новая колонка - начинаем с возрастания
      setSortColumn(date);
      setSortDirection('asc');
    }
  };

  const handleKeywordClick = () => {
    // В режиме видимости сортировка по запросу отключена
    if (filter === 'visibility') return;
    
    if (sortColumn === 'keyword') {
      // Переключаем направление: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      // Начинаем с возрастания (А-Я)
      setSortColumn('keyword');
      setSortDirection('asc');
    }
  };

  const handleFrequencyClick = () => {
    // В режиме видимости сортировка по частотности отключена
    if (filter === 'visibility') return;
    
    if (sortColumn === 'frequency') {
      // Переключаем направление: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      // Начинаем с убывания (от большей к меньшей)
      setSortColumn('frequency');
      setSortDirection('desc');
    }
  };

  return (
    <div className={styles.tableWrapper}>
      {/* === Плашки со статистикой === */}
      <div className={styles.statsGrid}>
        <StatItem 
          label="Средняя позиция" 
          value={averagePositionValue} 
          sub="" 
          color="blue"
          isActive={false}
          onClick={handleResetFilter}
        />
        <StatItem 
          label="Видимость" 
          value={visibilityValue} 
          color="blue"
          isActive={filter === 'visibility'}
          onClick={() => handleFilter('visibility')}
        />
        <StatItem
          label="Позиции улучшились"
          value={stats.improved_rating || 0}
          color="green"
          isActive={filter === 'improved'}
          onClick={() => handleFilter('improved')}
        />
        <StatItem
          label="Позиции ухудшились"
          value={stats.worsened_rating || 0}
          color="red"
          isActive={filter === 'worsened'}
          onClick={() => handleFilter('worsened')}
        />
        <StatItem 
          label="Топ 3" 
          value={stats.top_3 || 0} 
          color="blue"
          isActive={filter === 'top3'}
          onClick={() => handleFilter('top3')}
        />
        <StatItem 
          label="Топ 10" 
          value={stats.top_10 || 0} 
          color="blue"
          isActive={filter === 'top10'}
          onClick={() => handleFilter('top10')}
        />
        <StatItem
          label="Топ 30"
          value={stats.top_30 || 0}
          color="blue"
          isActive={filter === 'top30'}
          onClick={() => handleFilter('top30')}
        />
        <StatItem
          label="Топ 100"
          value={stats.top_100 || 0}
          color="blue"
          isActive={filter === 'top100'}
          onClick={() => handleFilter('top100')}
        />
        <StatItem
          label="Топ 100+"
          value={stats.top_100_plus || 0}
          color="blue"
          isActive={filter === 'top100plus'}
          onClick={() => handleFilter('top100plus')}
        />
      </div>

      <h3 className={styles.tableTitle}>
        Все ключевые слова
        {stats.total_keywords > 0 && (
          <span className={styles.tableTitleCount}>Запросов: {stats.total_keywords}</span>
        )}
      </h3>
      
      {loading ? (
        <div className={styles.loading}>
          <Loader />
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className={styles.loading}>
          {filter ? 'Нет ключевых слов для выбранного фильтра' : 'Нет данных'}
        </div>
      ) : (
      <>
      <div className={styles.horizontalScroll}>
        <table className={styles.tableHorizontal}>
          <thead>
            <tr>
              {/* <th>#</th> */}
              <TableNameItem 
                d="Запрос"
                onDateClick={filter === 'visibility' ? null : handleKeywordClick}
                isSorted={filter === 'visibility' ? false : sortColumn === 'keyword'}
                sortDirection={filter === 'visibility' ? null : (sortColumn === 'keyword' ? sortDirection : null)}
                style={{ width: '200px' }}
              />
              <TableNameItem 
                d="Частотность"
                onDateClick={filter === 'visibility' ? null : handleFrequencyClick}
                isSorted={filter === 'visibility' ? false : sortColumn === 'frequency'}
                sortDirection={filter === 'visibility' ? null : (sortColumn === 'frequency' ? sortDirection : null)}
                style={{ width: '120px' }}
              />
              {sortedDates.map((d) => {
                return (
                  <TableNameItem 
                    key={d} 
                    d={d} 
                    onDateClick={filter === 'visibility' ? null : handleDateClick}
                    isSorted={filter === 'visibility' ? false : sortColumn === d}
                    sortDirection={filter === 'visibility' ? null : (sortColumn === d ? sortDirection : null)}
                  />
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedFilteredEntries.map(([keyword, entries], index) => {
              const map = {};
              entries.forEach((entry) => {
                const date = moment(entry.date).format('DD.MM.YY');
                map[date] = entry;
              });

              // Получаем частотность за последнюю дату
              const getFrequency = (entries) => {
                if (!entries || entries.length === 0) return null;
                // Сортируем записи по дате (от новых к старым)
                const sortedEntries = [...entries].sort((a, b) => moment(b.date) - moment(a.date));
                // Ищем первое валидное значение total_shows
                for (const entry of sortedEntries) {
                  const freq = Number(entry.total_shows);
                  if (Number.isFinite(freq) && freq > 0) {
                    return freq;
                  }
                }
                return null;
              };

              const frequency = getFrequency(entries);

              return (
                <tr key={keyword}>
                  {/* <td>{index + 1}</td> */}
                  <td className={styles.keywordCell}>{keyword}</td>
                  <td>{frequency !== null ? frequency : '–'}</td>
                  {sortedDates.map((date) => {
                    const item = map[date];
                    if (!item) return <td key={date}>–</td>;

                    // Если режим видимости - показываем видимость
                    if (filter === 'visibility') {
                      const visibility = calculateKeywordVisibility(item, data, date);
                      if (visibility === null) {
                        return <td key={date}>–</td>;
                      }
                      return (
                        <td key={date}>
                          {visibility.toFixed(2)}
                        </td>
                      );
                    }

                    // Иначе показываем позиции
                    const { rating, change } = item;
                    let className = '';
                    if (typeof change === 'number') {
                      className = change > 0 
                        ? styles.positive 
                        : change < 0 
                        ? styles.negative 
                        : styles.equal;
                    }

                    return (
                      <td key={date} className={className}>
                        {rating}
                        {change !== null && change !== 0 && (
                          <sup>{change > 0 ? `-${change}` : `+${Math.abs(change)}`}</sup>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
};

// === Компонент плашки статистики ===
const StatItem = ({ label, value, sub, color, onClick, isActive }) => {
  const ringStyle = {
    borderTopColor: `var(--${color})`,
  };

  return (
    <div
      className={styles.statItem + (isActive ? ' ' + styles.statItemActive : '')}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <span className={styles.statLabel} style={{ color: `var(--${color})` }}>
        ● {label}
      </span>
      <div className={styles.statCircle}>
        <div className={styles.statValue}>{value}</div>
        {sub && <sup className={styles.statSub}>{sub}</sup>}
        <div className={styles.statRing} style={ringStyle}></div>
      </div>
    </div>
  );
};

const TableNameItem = ({ d, onDateClick, isSorted, sortDirection, style }) => {
  const getSortIcon = () => {
    if (!isSorted || !sortDirection) return '';
    return sortDirection === 'desc' ? ' ↓' : ' ↑';
  };

  return (
    <th
      onClick={onDateClick ? () => onDateClick(d) : undefined}
      className={isSorted ? styles.sortableThSorted : (onDateClick ? styles.sortableTh : styles.sortableThStatic)}
      style={{
        cursor: onDateClick ? 'pointer' : 'default',
        userSelect: 'none',
        ...style
      }}
    >
      {d}{getSortIcon()}
    </th>
  );
};

export default PositionsTableAll;
